// frontend/context/PlayerContext.js
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'ogg', 'm4v'];
const getFileExtension = (url) => {
    if (!url) return '';
    try {
        const pathname = new URL(url).pathname;
        const parts = pathname.split('.');
        if (parts.length < 2) return '';
        return parts[parts.length - 1].toLowerCase();
    } catch {
        const simpleParts = url.split('?')[0].split('.');
        if (simpleParts.length < 2) return '';
        return simpleParts[simpleParts.length - 1].toLowerCase();
    }
};

import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';

const PlayerContext = createContext(undefined);

export const usePlayerContext = () => {
    const context = useContext(PlayerContext);
    if (context === undefined) {
        throw new Error('usePlayerContext must be used within a PlayerProvider');
    }
    return context;
};

export const PlayerProvider = ({ children }) => {
    const [currentMedia, setCurrentMedia] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);

    const registerAudioRef = (ref) => {
        audioRef.current = ref;
    };

    const closePlayer = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.src = '';
        }
        setIsPlaying(false);
        setCurrentMedia(null);
    }, []);

    const playMedia = useCallback((media) => {
        const audio = audioRef.current;
        const extension = getFileExtension(media.url);
        if (VIDEO_EXTENSIONS.includes(extension)) {
            closePlayer();
            return;
        }
        if (!audio) {
            setCurrentMedia(media);
            setIsPlaying(false);
            return;
        }

        const isSameMedia = media.url === audio.src;

        if (!isSameMedia) {
            audio.src = media.url;
            audio.load();
            setCurrentMedia(media);
            setIsPlaying(true);
        } else if (isSameMedia && !isPlaying) {
            audio.play().catch(() => {});
            setIsPlaying(true);
        } else if (isSameMedia && isPlaying) {
            audio.pause();
            setIsPlaying(false);
        }
    }, [isPlaying, closePlayer]);

    const togglePlayPause = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || !currentMedia) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            audio.play().catch(() => {});
            setIsPlaying(true);
        }
    }, [isPlaying, currentMedia]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !currentMedia) return;

        const handleLoadedMetadata = () => {
            if (isPlaying) {
                audio.play().catch(() => setIsPlaying(false));
            }
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        return () => audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    }, [currentMedia, isPlaying]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleEnded = () => {
            setIsPlaying(false);
            closePlayer();
        };

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [closePlayer]);

    const value = {
        currentMedia,
        isPlaying,
        audioRef,
        playMedia,
        togglePlayPause,
        registerAudioRef,
        closePlayer,
    };

    return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};